import React from 'react';
import { Link } from 'react-router-dom';
import { comparisonData } from '../data/mock';
import { ArrowRight, Shield, DollarSign, Layers, Lock, Server } from 'lucide-react';

export const ComparisonPage = () => {
  return (
    <div className="grid-pattern bg-[var(--bg-primary)] min-h-screen pt-28 md:pt-36">
      {/* Header */}
      <section className="px-4 md:px-8 lg:px-12 pb-12 md:pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold mb-4 md:mb-6">
            How We <span className="text-blue-gradient">Compare</span>
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-white/70 max-w-2xl mx-auto">
            See how Parler Cloud stacks up against AWS, Azure, Google Cloud, and DigitalOcean.
          </p>
        </div>
      </section>

      {/* Comparison Content */}
      <section className="px-4 md:px-8 lg:px-12 pb-12 md:pb-20">
        <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
          {/* Pricing */}
          <div className="card-dark p-5 md:p-8">
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-5">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[rgba(42,95,246,0.1)] flex items-center justify-center flex-shrink-0">
                <DollarSign size={20} color="#2a5ff6" />
              </div>
              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold">Pricing Models</h3>
            </div>
            <p className="text-sm md:text-base text-white/70 leading-relaxed">
              <strong className="text-[#2a5ff6]">Parler Cloud</strong> offers transparent flat-rate packages with no hidden fees*. 
              You know exactly what you'll pay each month. Hyperscale providers like AWS, Azure, and Google Cloud often have complex 
              pay-as-you-go pricing with separate charges for compute, storage, bandwidth, and security services.
            </p>
          </div>

          {/* Compute Architecture */}
          <div className="card-dark p-5 md:p-8">
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-5">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[rgba(42,95,246,0.1)] flex items-center justify-center flex-shrink-0">
                <Server size={20} color="#2a5ff6" />
              </div>
              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold">Compute Architecture</h3>
            </div>
            <p className="text-sm md:text-base text-white/70 leading-relaxed">
              <strong className="text-[#2a5ff6]">Parler Cloud</strong> delivers bare-metal performance via SmartOS virtualization 
              using zones and containers. Our open-source architecture provides high performance and cost savings compared to proprietary 
              hypervisors used by AWS (Nitro), Azure (Hyper-V), and Google Cloud.
            </p>
          </div>

          {/* Security */}
          <div className="card-dark p-5 md:p-8">
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-5">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[rgba(42,95,246,0.1)] flex items-center justify-center flex-shrink-0">
                <Shield size={20} color="#2a5ff6" />
              </div>
              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold">CDN & Security Integration</h3>
            </div>
            <p className="text-sm md:text-base text-white/70 leading-relaxed">
              <strong className="text-[#2a5ff6]">Parler Cloud</strong> includes integrated CDN and security with Edgecast—featuring 
              WAF, DDoS protection, and API security in every plan. Other providers require separate services and additional charges.
            </p>
          </div>

          {/* Storage */}
          <div className="card-dark p-5 md:p-8">
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-5">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[rgba(42,95,246,0.1)] flex items-center justify-center flex-shrink-0">
                <Layers size={20} color="#2a5ff6" />
              </div>
              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold">Storage</h3>
            </div>
            <p className="text-sm md:text-base text-white/70 leading-relaxed">
              <strong className="text-[#2a5ff6]">Parler Cloud</strong> bundles high-performance NVMe storage with object storage 
              in every package. Hyperscale providers charge separately for block and object storage, making cost predictions difficult.
            </p>
          </div>

          {/* Data Sovereignty */}
          <div className="card-dark p-5 md:p-8">
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-5">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[rgba(42,95,246,0.1)] flex items-center justify-center flex-shrink-0">
                <Lock size={20} color="#2a5ff6" />
              </div>
              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold">Data Sovereignty & Transparency</h3>
            </div>
            <p className="text-sm md:text-base text-white/70 leading-relaxed">
              <strong className="text-[#2a5ff6]">Parler Cloud</strong> runs on open-source software and Open Compute hardware, 
              providing complete transparency into your infrastructure. Note: Parler Cloud focuses on core infrastructure rather 
              than specialized AI/ML services.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="px-4 md:px-8 lg:px-12 pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-6 md:mb-10 text-center">
            At a <span className="text-blue-gradient">Glance</span>
          </h2>
          
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="comparison-table min-w-[800px] w-full rounded-xl overflow-hidden">
              <thead>
                <tr>
                  <th className="min-w-[140px]">Category</th>
                  <th className="min-w-[140px] bg-[#2a5ff6]/10 border-x border-[#2a5ff6]/30">
                    <span className="text-[#2a5ff6] text-lg font-bold">Parler Cloud</span>
                  </th>
                  <th className="min-w-[120px]">AWS</th>
                  <th className="min-w-[120px]">Azure</th>
                  <th className="min-w-[120px]">Google Cloud</th>
                  <th className="min-w-[120px]">DigitalOcean</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.categories.map((row, idx) => (
                  <tr key={idx}>
                    <td className="font-medium">{row.name}</td>
                    <td className="text-[#2a5ff6] font-medium bg-[#2a5ff6]/5 border-x border-[#2a5ff6]/20">{row.parlerCloud}</td>
                    <td className="text-white">{row.aws}</td>
                    <td className="text-white">{row.azure}</td>
                    <td className="text-white">{row.googleCloud}</td>
                    <td className="text-white">{row.digitalOcean}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footnote */}
          <p className="text-center mt-6 md:mt-8 p-4 bg-[var(--bg-secondary)] border border-white/10 text-white/50 text-xs md:text-sm rounded-xl">
            Powered by <span className="text-[#2a5ff6]">Triton Cloud</span> and <span className="text-[#2a5ff6]">Open Compute</span>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 md:px-8 lg:px-12 pb-16 md:pb-24">
        <div className="max-w-3xl mx-auto text-center p-8 md:p-12 bg-[var(--bg-secondary)] border border-[#2a5ff6] rounded-2xl">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-3 md:mb-4">
            Ready to make the switch?
          </h2>
          <p className="text-sm md:text-base text-white/70 mb-6 md:mb-8">
            Experience transparent pricing, superior performance, and integrated security.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/packages" className="btn-primary justify-center">
              View Packages
              <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="btn-secondary justify-center">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Disclaimer */}
      <section className="px-4 md:px-8 lg:px-12 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs text-white/40 text-center leading-relaxed">
            *Prices exclude applicable taxes, regulatory charges, and third-party pass-through fees.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ComparisonPage;
